import { useState } from 'react';
import { Copy, Check, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface BoletoPaymentProps {
  boletoBarcode: string | null;
  boletoPdfUrl: string | null;
  amount: number;
}

export function BoletoPayment({ boletoBarcode, boletoPdfUrl }: BoletoPaymentProps) {
  const [copied, setCopied] = useState(false);

  // Generate placeholder barcode if not provided
  const displayBarcode = boletoBarcode || '23793.38128 60000.000003 00000.000400 1 84340000012500';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(displayBarcode.replace(/\D/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Generate barcode visualization - ITF pattern for boleto
  const generateBarcodePattern = () => {
    const code = displayBarcode.replace(/\D/g, '');
    const bars = [];
    let x = 0;
    const totalBars = 120;
    
    // Start pattern
    bars.push(<rect key="start1" x={x} y="0" width="2" height="60" fill="currentColor" />);
    x += 4;
    bars.push(<rect key="start2" x={x} y="0" width="2" height="60" fill="currentColor" />);
    x += 6;

    // Generate pseudo-random bar pattern based on code
    for (let i = 0; i < totalBars; i++) {
      const charCode = code.charCodeAt(i % code.length) || 48;
      const width = ((charCode + i) % 3) + 1;
      const gap = ((charCode * i) % 2) + 2;
      
      bars.push(
        <rect 
          key={`bar-${i}`}
          x={x}
          y="0"
          width={width}
          height="60"
          fill="currentColor"
        />
      );
      x += width + gap;
    }

    // End pattern
    bars.push(<rect key="end1" x={x} y="0" width="3" height="60" fill="currentColor" />);
    x += 5;
    bars.push(<rect key="end2" x={x} y="0" width="2" height="60" fill="currentColor" />);
    x += 4;

    return { bars, totalWidth: x };
  };

  const { bars, totalWidth } = generateBarcodePattern();

  return (
    <div className="space-y-6">
      {/* Barcode Visual */}
      <div className="flex flex-col items-center">
        <div className="w-full bg-white rounded-lg p-6 shadow-md border border-[hsl(var(--basic-200))]">
          {/* Barcode - Full width horizontal */}
          <div className="w-full overflow-hidden">
            <svg 
              viewBox={`0 0 ${totalWidth} 60`}
              className="w-full h-14 text-[hsl(var(--basic-900))]"
              preserveAspectRatio="none"
            >
              {bars}
            </svg>
          </div>
          
          {/* Linha digitável - dashed border above numbers */}
          <div className="mt-4 pt-4 border-t-2 border-dashed border-[hsl(var(--basic-300))]">
            <p className="text-center font-mono text-sm text-[hsl(var(--basic-800))] tracking-[0.15em]">
              {displayBarcode}
            </p>
          </div>
        </div>
      </div>

      {/* Copy Code Section */}
      <div>
        <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
          Linha Digitável
        </label>
        <div className="flex gap-2">
          <div className="flex-1 p-4 bg-[hsl(var(--basic-50))] rounded-xl border-2 border-dashed border-[hsl(var(--basic-200))] font-mono text-sm tracking-wider text-[hsl(var(--basic-700))]">
            {displayBarcode}
          </div>
          <Button 
            onClick={handleCopy}
            className={`h-auto px-6 transition-all ${
              copied 
                ? 'bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))]' 
                : 'bg-[hsl(var(--basic-900))] hover:bg-[hsl(var(--basic-800))]'
            }`}
          >
            {copied ? (
              <span className="flex items-center gap-2">
                <Check className="h-4 w-4" />
                Copiado!
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Copy className="h-4 w-4" />
                Copiar
              </span>
            )}
          </Button>
        </div>
        {copied && (
          <p className="text-sm text-[hsl(var(--accent-600))] mt-2 text-center font-medium">
            ✓ Código copiado! Cole no app do seu banco.
          </p>
        )}
      </div>

      {/* Download PDF Button */}
      <Button 
        variant="outline" 
        className="w-full gap-2 h-12 border-2 hover:bg-[hsl(var(--basic-50))]"
        onClick={() => {
          if (boletoPdfUrl) {
            window.open(boletoPdfUrl, '_blank');
          } else {
            alert('PDF do boleto não disponível');
          }
        }}
      >
        <Download className="h-5 w-5" />
        Baixar Boleto em PDF
      </Button>

      {/* Instructions */}
      <div className="bg-gradient-to-br from-[hsl(var(--basic-50))] to-[hsl(var(--basic-100))] rounded-xl p-5 border">
        <h4 className="font-bold text-sm text-[hsl(var(--basic-800))] mb-3 flex items-center gap-2">
          <span className="h-6 w-6 rounded-full bg-[hsl(var(--basic-500))] text-white flex items-center justify-center text-xs">?</span>
          Como pagar o boleto:
        </h4>
        <ol className="text-sm text-[hsl(var(--basic-600))] space-y-2">
          <li className="flex items-start gap-3">
            <span className="h-5 w-5 rounded-full bg-[hsl(var(--basic-200))] text-[hsl(var(--basic-600))] flex items-center justify-center text-xs font-bold shrink-0">1</span>
            <span>Copie a linha digitável acima</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="h-5 w-5 rounded-full bg-[hsl(var(--basic-200))] text-[hsl(var(--basic-600))] flex items-center justify-center text-xs font-bold shrink-0">2</span>
            <span>Abra o app do seu banco</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="h-5 w-5 rounded-full bg-[hsl(var(--basic-200))] text-[hsl(var(--basic-600))] flex items-center justify-center text-xs font-bold shrink-0">3</span>
            <span>Escolha pagar boleto e cole o código</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="h-5 w-5 rounded-full bg-[hsl(var(--basic-200))] text-[hsl(var(--basic-600))] flex items-center justify-center text-xs font-bold shrink-0">4</span>
            <span>Confirme o pagamento</span>
          </li>
        </ol>
        <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-[hsl(var(--basic-200))]">
          ⏱ O boleto pode levar até 3 dias úteis para compensar.
        </p>
      </div>
    </div>
  );
}
